import type { LifetimeParameters, ShapeDescriptor } from '@hub/bearing-hub/common/types';
import { getLocale, MetricType } from '@hub/libs/constants';
import type { ParsedUrlQuery } from 'querystring';
import { queryParamsHandler } from '@hub/bearing-hub/BearingHubPage/libs/queryParamsHandler';
import { BearingHubHelper, SHAPE_DESCRIPTORS, SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import type { GetServerSidePropsContext, PreviewData } from 'next';
import type { LanguageAlternate, ProductDetailsParams } from '@hub/models/data';
import { getArticleName } from '@hub/bearing-hub/common/articleName.factory';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import type {
  IbhCatalogArticleMatrixDetailsDto,
  IbhCatalogArticleMatrixShapesDto,
  IbhPartsResponseDto,
  IbhShape,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { fetchCatalogArticlesMap } from '@hub/bearing-hub/common/fetchCatalogArticlesMap';
import { bearingShapes } from '@igus/iglidur-bearing-domain';

export const appPrefix = 'iglidur';
export const productDetailsSlug = '/product-details/';

// FIXME start using IbhUnitSystem
export const getCategoryName = (materialId: string, metric: MetricType) =>
  encodeURIComponent(`${appPrefix}-${materialId.toLowerCase()}-${metric.charAt(0)}`);
export enum IbhProductionMethodCode {
  IM = 'IM',
  SF = 'SF',
  MR = 'MR',
  FL = 'FL',
  ThreeD = '3D',
}

const mapProductionMethod = (code?: string): IbhProductionMethods => {
  switch ((code || IbhProductionMethodCode.IM).toUpperCase()) {
    case IbhProductionMethodCode.IM:
      return IbhProductionMethods.MOLDINJECTION;
    case IbhProductionMethodCode.SF:
      return IbhProductionMethods.SEMIFINISHED;
    case IbhProductionMethodCode.MR:
      return IbhProductionMethods.MECHANICALREPROCESSING;
    case IbhProductionMethodCode.FL:
      return IbhProductionMethods.FASTLINE;
    case IbhProductionMethodCode.ThreeD:
      return IbhProductionMethods.THREEDIMENSIONALPRINT;
    default:
      return IbhProductionMethods.MOLDINJECTION;
  }
};

export const getArticleDataFromContext = (
  context: GetServerSidePropsContext<ProductDetailsParams, PreviewData>
): {
  materialName: string;
  unitSystem: IbhUnitSystem;
  isCustom: boolean;
  selectedShapeDescriptor: ShapeDescriptor;
  productionMethod: IbhProductionMethods;
} => {
  const { shape } = context.params;
  const slug = context.params.categoryName;
  const artnr = context.query?.artnr as string;
  const locale = getLocale(context);
  const selectedShapeDescriptor = BearingHubHelper.getShapeByShapePath(shape, locale, bearingShapes);
  const customArticlePrefix = 'TEMP';

  // [ 'i180', 'bl', 'pf', 'm' ] or [ 'g', 'm' ] example of slugPieces
  const slugPieces = slug.replace(`${appPrefix}-`, '').split('-');
  const isCustom = artnr?.includes(customArticlePrefix) || false;
  const productionMethodFromSlug = isCustom ? artnr.match(/^TEMP-([^-]+)/)[1] : 'IM';
  const productionMethod = mapProductionMethod(productionMethodFromSlug);
  const unitSystemPiece = slugPieces.slice(-1)[0];
  const materialName = slugPieces.slice(0, -1).join('-').toUpperCase();
  const unitSystem = unitSystemPiece === 'm' ? IbhUnitSystem.Metric : IbhUnitSystem.Imperial;

  return {
    materialName,
    unitSystem,
    isCustom,
    selectedShapeDescriptor,
    productionMethod,
  };
};

export const getRedirectLinkToProductDetails = (
  part: IbhPartsResponseDto,
  selectedShape: ShapeDescriptor,
  metric: MetricType,
  hashParam: ParsedUrlQuery,
  isCustom: boolean,
  locale: string
) => {
  const pathNameToResultPage = `${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${
    selectedShape.shapePath
  }`;
  const pathNameToProductDetailsPage = 'product-details';
  const categoryName = getCategoryName(part.material.id, metric);
  const properArticleNumber = getArticleName(locale, part);

  const query = isCustom
    ? `${properArticleNumber}_${metric.toUpperCase()}${BearingHubHelper.getDimensionsParam(
        part.dimensions,
        selectedShape
      )}`
    : properArticleNumber;
  const hash = queryParamsHandler.createHashParam(hashParam).toString();
  const articleNumber = part.smallestForMaterial ? '' : `?artnr=${query}`;

  return `/${pathNameToResultPage}/${pathNameToProductDetailsPage}/${categoryName}${articleNumber}${
    hash ? `#${hash}` : ''
  }`;
};

export const getCanonicalLink = (languageAlternates: LanguageAlternate[], locale: string) =>
  languageAlternates.find(item => item.hrefLang === locale)?.href.split('?')[0];

export const getRedirectLinkToResults = (
  selectedShape: ShapeDescriptor,
  quantity: number,
  lifetimeParameters: LifetimeParameters,
  locale: string,
  unitSystem: IbhUnitSystem
) => {
  const pathNameToResultPage = `${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${
    selectedShape.shapePath
  }`;
  const lifeTime = queryParamsHandler.getServiceLifeParams(lifetimeParameters, unitSystem);
  const lifeTimeHash = lifeTime ? `lifetimeParameters=${lifeTime}` : '';
  const quantityHash = quantity > 1 ? `quantity=${quantity}` : '';
  const hasLifeTimeAndQuantityHash = lifeTimeHash && quantityHash ? '&' : '';
  const hash = quantity > 1 || lifeTimeHash ? `#${quantityHash}${hasLifeTimeAndQuantityHash}${lifeTimeHash}` : '';
  return `/${pathNameToResultPage}${hash}`;
};

export const getLinkForResultPage = (shape: IbhShape, locale: string) => {
  const shapeDescriptor = SHAPE_DESCRIPTORS[locale];
  const shapePath = shapeDescriptor[shape].path;
  const defaultPathToResultPage = `${
    SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'
  }/${shapePath}`;

  return `/${defaultPathToResultPage}`;
};

export const getLinkForDetailPage = (article: IbhCatalogArticleMatrixDetailsDto, shape: IbhShape, locale: string) => {
  const { materialCode, unitSystem } = article;
  const pathToResultPage = getLinkForResultPage(shape, locale);
  const unit = unitSystem === IbhUnitSystem.Metric ? MetricType.MM : MetricType.INCH;
  const categoryName = getCategoryName(materialCode, unit);

  return `${pathToResultPage}${productDetailsSlug}${categoryName}`;
};

export const getLinksForShape = (articles: IbhCatalogArticleMatrixDetailsDto[], shape: IbhShape, locale: string) => {
  const linkToResultPage = getLinkForResultPage(shape, locale);
  const linksToProductDetailsPage = articles?.map(article => getLinkForDetailPage(article, shape, locale));

  if (!linksToProductDetailsPage) {
    return [linkToResultPage];
  }
  return [linkToResultPage, ...linksToProductDetailsPage];
};

/**
 * Return all IBH links
 *
 * @param shapes - list of supported shapes (e.g. [IbhShape.S, IbhShape.F])
 * @param locale - given locale (e.g. 'en-US')
 */
export const getIbhSiteMap = async (locale: string, shapes?: IbhShape[]) => {
  const normalizedLocale = locale.includes('-') ? locale.split('-')[1] : locale.toUpperCase();
  const defaultShapesList = Object.keys(SHAPE_DESCRIPTORS[locale]) as IbhShape[];
  const shapeToUse = shapes ?? defaultShapesList;
  const articlesMatrix = await fetchCatalogArticlesMap(shapes, [normalizedLocale]);
  const articlesMap = (articlesMatrix[normalizedLocale] as IbhCatalogArticleMatrixShapesDto) || articlesMatrix.default;

  return shapeToUse.flatMap(shape => getLinksForShape(articlesMap[shape], shape, locale));
};
